import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, FileCode2, FileDown } from "lucide-react";
import { api, fmt, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { PageHeader, Card, Btn, inp } from "@/components/ui-kit";
import { SdiBadge } from "@/components/InvoiceModal";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function Invoices() {
  const { restaurant } = useAuth();
  const { t } = useT();
  const rid = restaurant.id;
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get(`/restaurants/${rid}/invoices`, { params: { month } }).then((x) => setD(x.data)), [rid, month]);
  useEffect(() => { load(); }, [load]);
  const refresh = async (inv) => {
    setBusy(inv.id);
    try { await api.post(`/restaurants/${rid}/invoices/${inv.id}/refresh`); await load(); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(null); }
  };
  const dl = async (inv, kind) => {
    try {
      const res = await api.get(`/restaurants/${rid}/invoices/${inv.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `Fattura_${inv.number.replace("/", "_")}.${kind}`; a.click(); URL.revokeObjectURL(url);
    } catch (ex) { toast.error(errMsg(ex)); }
  };
  if (!d) return null;
  return (
    <div data-testid="invoices-page">
      <PageHeader title={t("nav_invoices")}>
        <input data-testid="invoices-month-input" type="month" className={inp + " w-auto"} value={month} onChange={(e) => setMonth(e.target.value)} />
      </PageHeader>
      {!d.provider_configured && <p data-testid="invoices-provider-warning" className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{t("invoice_provider_missing")}</p>}
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl bg-slate-900 p-4 text-white"><p className="text-xs font-semibold uppercase text-slate-400">{t("invoices_month")}</p><p data-testid="invoices-count" className="font-heading text-3xl font-extrabold">{d.count}</p></div>
        <div className="rounded-2xl bg-slate-900 p-4 text-white"><p className="text-xs font-semibold uppercase text-slate-400">{t("invoiced_total")}</p><p data-testid="invoices-total" className="font-heading text-3xl font-extrabold">{fmt(d.total)}</p></div>
        <Card className="flex flex-wrap items-center gap-2">{Object.entries(d.by_status).filter(([, n]) => n > 0).map(([s, n]) => <span key={s} className="flex items-center gap-1 text-xs"><SdiBadge status={s} t={t} /> {n}</span>)}{d.count === 0 && <span className="text-sm text-slate-500">—</span>}</Card>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">{t("n")}</th><th>{t("date")}</th><th>{t("customer")}</th><th>{t("ref_doc")}</th><th>{t("sdi_status_label")}</th><th className="text-right">{t("total")}</th><th></th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {d.invoices.map((inv) => <tr key={inv.id} data-testid={`invoice-row-${inv.seq}`}>
              <td className="py-2 font-mono font-semibold">{inv.number}</td><td>{inv.date}</td>
              <td><p className="font-semibold">{inv.customer.name}</p><p className="text-xs text-slate-500">{inv.customer.vat_number || inv.customer.fiscal_code}</p></td>
              <td className="font-mono text-xs">{inv.receipt_document_number || `#${String(inv.receipt_number).padStart(4, "0")}`}</td>
              <td><SdiBadge status={inv.sdi_status} t={t} />{inv.notifications?.length > 0 && <p className="mt-0.5 max-w-[220px] truncate text-[10px] text-slate-500" title={inv.notifications[0].message}>{inv.notifications[0].type}: {inv.notifications[0].message}</p>}{inv.sdi_error && <p className="mt-0.5 max-w-[220px] truncate text-[10px] text-rose-600" title={inv.sdi_error}>{inv.sdi_error}</p>}</td>
              <td className="text-right font-semibold">{fmt(inv.total)}</td>
              <td className="text-right"><div className="flex justify-end gap-1">
                <button data-testid={`invoice-refresh-${inv.seq}`} title={t("refresh_status")} onClick={() => refresh(inv)} className="rounded-lg p-1.5 hover:bg-slate-100"><RefreshCw size={15} className={busy === inv.id ? "animate-spin" : ""} /></button>
                <button data-testid={`invoice-xml-${inv.seq}`} title="XML" onClick={() => dl(inv, "xml")} className="rounded-lg p-1.5 hover:bg-slate-100"><FileCode2 size={15} /></button>
                {inv.acube_uuid && <a data-testid={`invoice-pdf-${inv.seq}`} title="PDF" href={`${BASE}/api/restaurants/${rid}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" className="rounded-lg p-1.5 hover:bg-slate-100"><FileDown size={15} /></a>}
              </div></td>
            </tr>)}
          </tbody>
        </table>
        {d.invoices.length === 0 && <p className="py-4 text-center text-sm text-slate-500">{t("no_invoices")}</p>}
      </Card>
    </div>
  );
}
